// Customer login on app.aleva.se: an emailed one-time link. The link opens
// this page with ?token=…; the "Continue" button posts it (so a mail
// scanner fetching the link cannot use it up) and the API sets the session
// cookie. Invitations work the same way on /invite.
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { activeOrg, api, ApiError } from '../api'

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="gate">
      <div className="card auth-card">
        <div className="brand">
          <span className="mark" aria-hidden>
            <i />
            <i />
            <i />
            <i />
          </span>
          Aleva
        </div>
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  )
}

const linkError = (e: Error | null) =>
  e instanceof ApiError && e.status === 400
    ? 'The link is invalid, used or has expired. Ask for a new one below.'
    : e
      ? `Something went wrong: ${e.message}`
      : null

const IDP_ERRORS: Record<string, string> = {
  idp_unavailable: 'The sign-in service is not reachable right now. Try again in a moment.',
  idp_refused: 'The sign-in was not accepted. Try again.',
  expired: 'The sign-in took too long. Try again.',
  no_email: 'Your account has no verified email address.',
  no_org: 'Your account is not a member of any organisation here. Ask an administrator for access.',
  disabled: 'Your account is disabled.',
}

export function LoginPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token')
  const idpError = IDP_ERRORS[params.get('error') ?? '']
  const conf = useQuery({ queryKey: ['auth-config'], queryFn: api.authConfig, retry: false })
  const [email, setEmail] = useState('')
  const send = useMutation({ mutationFn: () => api.login(email) })
  const use = useMutation({
    mutationFn: () => api.loginCallback(token ?? ''),
    onSuccess: () => {
      activeOrg.set('')
      navigate('/', { replace: true })
    },
  })

  if (token && !use.isError) {
    return (
      <Card title="Log in">
        <p>Continue to log in to Aleva.</p>
        <button className="btn btn-primary" disabled={use.isPending} onClick={() => use.mutate()}>
          {use.isPending ? 'Logging in…' : 'Continue'}
        </button>
      </Card>
    )
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    send.mutate()
  }
  return (
    <Card title="Log in">
      {idpError && <p className="callout err">{idpError}</p>}
      {conf.data?.oidc && (
        <>
          <a className="btn btn-primary" href="/admin-api/auth/oidc/start">
            Log in with your organisation's account
          </a>
          <p className="note">Or get a login link by email:</p>
        </>
      )}
      {linkError(use.error) && <p className="callout err">{linkError(use.error)}</p>}
      {send.isSuccess ? (
        <p className="callout ok">If {email} belongs to an organisation on Aleva, a login link is on its way. It is valid for 15 minutes.</p>
      ) : (
        <form className="col" onSubmit={submit}>
          <label>
            Email address
            <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {send.error && (
            <p className="callout err">
              {send.error instanceof ApiError && send.error.status === 429 ? 'Too many attempts. Wait a few minutes.' : send.error.message}
            </p>
          )}
          <button className="btn btn-primary" type="submit" disabled={send.isPending}>
            {send.isPending ? 'Sending…' : 'Email me a login link'}
          </button>
        </form>
      )}
      <p className="note">No password: we email you a link each time. Access is by invitation from your organisation's admin.</p>
    </Card>
  )
}

export function InvitePage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const join = useMutation({
    mutationFn: () => api.acceptInvite(token),
    onSuccess: () => {
      activeOrg.set('')
      navigate('/', { replace: true })
    },
  })
  return (
    <Card title="Join your organisation">
      {join.error ? (
        <p className="callout err">
          {join.error instanceof ApiError && join.error.status === 400
            ? 'The invitation is invalid, already used or has expired. Ask your admin for a new one.'
            : join.error.message}
        </p>
      ) : (
        <>
          <p>You have been invited to an organisation on Aleva. Continue to join and log in.</p>
          <button className="btn btn-primary" disabled={join.isPending || !token} onClick={() => join.mutate()}>
            {join.isPending ? 'Joining…' : 'Join and continue'}
          </button>
        </>
      )}
    </Card>
  )
}
